import type {
  EngagedRange,
  LayoutManager,
} from './layout-managers/LayoutManager';

export type { EngagedRange };

export interface EngagedIndicesTrackerOptions {
  layoutManager: LayoutManager;
  drawDistance?: number;
}

// Matches upstream FlashList v2's web default
// (src/native/config/PlatformHelper.web.ts: defaultDrawDistance = 500).
const DEFAULT_DRAW_DISTANCE = 500;

const VELOCITY_HISTORY_SIZE = 5;
// Buffer split — upstream values from RVEngagedIndicesTrackerImpl. 30% sits
// in the direction we're scrolling away from, 70% pre-renders ahead.
const SCROLL_DIRECTION_BIAS = 0.7;
const SCROLL_OPPOSITE_BIAS = 0.3;

export class EngagedIndicesTracker {
  private readonly layoutManager: LayoutManager;
  private drawDistance: number;

  private readonly velocityHistory = new Array<number>(
    VELOCITY_HISTORY_SIZE,
  ).fill(0);
  private velocityIndex = 0;
  private lastRecordedOffset = 0;

  constructor(options: EngagedIndicesTrackerOptions) {
    this.layoutManager = options.layoutManager;
    this.drawDistance = options.drawDistance ?? DEFAULT_DRAW_DISTANCE;
  }

  setDrawDistance(drawDistance: number): void {
    this.drawDistance = drawDistance;
  }

  /**
   * Record a new scroll position. Subsequent computeEngagedRange calls use
   * the resulting velocity history to weight overscan toward scroll direction.
   * The scroll handler should call this on every scroll event, then call
   * computeEngagedRange to read the resulting range.
   */
  recordScroll(scrollOffset: number): void {
    const delta = scrollOffset - this.lastRecordedOffset;
    this.lastRecordedOffset = scrollOffset;
    this.velocityHistory[this.velocityIndex] = delta;
    this.velocityIndex = (this.velocityIndex + 1) % VELOCITY_HISTORY_SIZE;
  }

  computeEngagedRange(
    scrollOffset: number,
    viewport: number,
  ): EngagedRange | null {
    const count = this.layoutManager.itemCount;
    if (count === 0) return null;
    // No real viewport — fall back to rendering everything. Keeps SSR /
    // pre-mount renders deterministic.
    if (viewport === 0) return { start: 0, end: count - 1 };

    const totalBuffer = this.drawDistance * 2;
    const direction = this.computeDirection();

    let bufferBefore: number;
    let bufferAfter: number;
    if (direction === 'forward') {
      bufferBefore = Math.ceil(totalBuffer * SCROLL_OPPOSITE_BIAS);
      bufferAfter = Math.ceil(totalBuffer * SCROLL_DIRECTION_BIAS);
    } else if (direction === 'backward') {
      bufferBefore = Math.ceil(totalBuffer * SCROLL_DIRECTION_BIAS);
      bufferAfter = Math.ceil(totalBuffer * SCROLL_OPPOSITE_BIAS);
    } else {
      // No scroll history yet — split symmetrically. Matches old behavior
      // and keeps the synchronous test cases independent of velocity state.
      bufferBefore = this.drawDistance;
      bufferAfter = this.drawDistance;
    }

    // Boundary redistribution — if the buffer would extend past the start
    // or end of the content, push the excess to the other side so a list at
    // its very top or very bottom still renders the full window.
    let extendedStart = Math.max(0, scrollOffset - bufferBefore);
    const unusedStartBuffer = Math.max(0, bufferBefore - scrollOffset);
    let extendedEnd = scrollOffset + viewport + bufferAfter + unusedStartBuffer;

    const dimensions = this.layoutManager.getContentSize();
    const contentSize = this.layoutManager.isHorizontal()
      ? dimensions.width
      : dimensions.height;
    if (extendedEnd > contentSize) {
      const unusedEndBuffer = extendedEnd - contentSize;
      extendedEnd = contentSize;
      extendedStart = Math.max(0, extendedStart - unusedEndBuffer);
    }

    return this.layoutManager.findIndicesInRange(extendedStart, extendedEnd);
  }

  /** Test seam — reset velocity history to its zero-state. */
  resetVelocityHistory(): void {
    this.velocityHistory.fill(0);
    this.velocityIndex = 0;
    this.lastRecordedOffset = 0;
  }

  private computeDirection(): 'forward' | 'backward' | 'unknown' {
    let positive = 0;
    let negative = 0;
    for (const value of this.velocityHistory) {
      if (value > 0) positive++;
      else if (value < 0) negative++;
    }
    if (positive === 0 && negative === 0) return 'unknown';
    return negative > positive ? 'backward' : 'forward';
  }
}
